import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';

import { Product, getTheme, getPdtUid } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { ACTIVATE_PATIENT } from '@/apollo/mutations';
import { logError } from '@/utils/logError';

/**
 * Extract entitlement status from a Supabase JWT.
 * Returns 'active', 'pending', or undefined if not present.
 */
const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { search } = useLocation();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });
  const [activatePatient] = useMutation(ACTIVATE_PATIENT, { errorPolicy: 'all' });

  useEffect(() => {
    // Login flow: user already active — skip activation, go straight to session handoff.
    const handleLoginFlow = async () => {
      console.log('[EmailAuthCallback][handleLoginFlow] refreshing session...');
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        logError('[EmailAuthCallback]', 'refreshSession failed', refreshError);
        history.replace('/spark/login');
        return;
      }
      console.log('[EmailAuthCallback][handleLoginFlow] session refreshed OK');

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
      console.log('[EmailAuthCallback][handleLoginFlow] redirectTo from sessionStorage:', redirectTo);

      // Browser context: no in-app deep link available — route to download page.
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        console.log('[EmailAuthCallback][handleLoginFlow] → PATH: download-app (no redirectTo deep link)');
        history.replace(`/activate/${product}/download-app`);
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
        console.log('[EmailAuthCallback][handleLoginFlow] exchangeToken obtained');
      } catch (err) {
        logError('[EmailAuthCallback]', 'requestAuthCode failed', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: {
          headers: { Authorization: `Bearer ${refreshedSession.access_token}` },
        },
      });
      const limbixUserUid = meData?.me?.uid;
      console.log('[EmailAuthCallback][handleLoginFlow] limbixUserUid:', limbixUserUid);

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      console.log('[EmailAuthCallback][handleLoginFlow] → PATH: deep link handoff →',
        deepLinkUrl.toString());
      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        // In-app browser context: Spark appends redirectTo to the verify URL's
        // redirect_to param so it arrives here as a search param. Store it so
        // downstream handoff can use it for the deep link back to the app.
        const redirectToParam = new URLSearchParams(search).get('redirectTo');
        console.log('[EmailAuthCallback] redirectToParam:', redirectToParam);
        if (redirectToParam && isSafeRedirectUri(redirectToParam)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
          console.log('[EmailAuthCallback] redirectTo stored in sessionStorage:', redirectToParam);
        }

        // Wait for Supabase to process the hash fragment (detectSessionInUrl is async)
        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();
        console.log('[EmailAuthCallback] session:', session ? 'EXISTS' : 'NULL');

        if (!session) {
          console.warn('[EmailAuthCallback] No session — redirecting to access-code with error');
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';
        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;
        const isDependent = session.user?.user_metadata?.isDependent;

        console.log('[EmailAuthCallback] user_metadata:', session.user?.user_metadata);
        console.log('[EmailAuthCallback] supabaseUserId:', supabaseUserId);
        console.log('[EmailAuthCallback] patientUid (limbixUserId):', patientUid);
        console.log('[EmailAuthCallback] entitlementStatus:', entitlementStatus);
        console.log('[EmailAuthCallback] accessCode:', accessCode);
        console.log('[EmailAuthCallback] isDependent (from metadata):', isDependent);

        // If access code exists in metadata, this is an activation flow.
        // After activation completes, Vercel clears accessCode from metadata.
        // So returning users won't have it → login handoff.
        const isReturningUser = !accessCode && entitlementStatus === 'active';
        console.log('[EmailAuthCallback] isReturningUser:', isReturningUser,
          '| no accessCode:', !accessCode, '| status active:', entitlementStatus === 'active');

        if (isReturningUser) {
          console.log('[EmailAuthCallback] → PATH: handleLoginFlow (returning user / already active)');
          await handleLoginFlow();
          return;
        }

        // Activation flow: confirm entitlement in Supabase.
        // activateUserByAccessCode is called later in UserInfoConfirmation after demographics.
        console.log('[EmailAuthCallback] → calling activateSparkConfirm...');
        try {
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });
          console.log('[EmailAuthCallback] activateSparkConfirm: OK');
        } catch (err) {
          logError('[EmailAuthCallback]', 'activation failed', err, { supabaseUserId });
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        // Dependent flow: all info already collected by caregiver — activate patient then go to app.
        if (isDependent) {
          const orgSlug = new URLSearchParams(search).get('org_slug');
          console.log('[EmailAuthCallback] dependent — calling activatePatient, orgSlug:', orgSlug);
          const activateResponse = await activatePatient({
            variables: {
              pdtUid: getPdtUid(product),
              organizationSlug: orgSlug,
            },
            context: {
              headers: { Authorization: `Bearer ${session.access_token}` },
            },
          });
          if (!activateResponse.data?.activatePatient?.success) {
            logError('[EmailAuthCallback]', 'activatePatient failed for dependent', undefined, { orgSlug });
          }
          console.log('[EmailAuthCallback] → PATH: handleLoginFlow (dependent user)');
          await handleLoginFlow();
          return;
        }

        const orgSlug = new URLSearchParams(search).get('org_slug');
        console.log('[EmailAuthCallback] orgSlug from URL:', orgSlug);

        // Employer flow (org slug but no access code): go to coverage check first.
        // Access code flow (has access code, may also have org slug from Cortex): skip coverage check.
        if (orgSlug && !accessCode) {
          console.log('[EmailAuthCallback] → PATH: coverage-check (employer flow, no access code)');
          history.replace(`/activate/${product}/coverage-check/${orgSlug}`, {
            patientUid,
            flow: 'email',
            accessToken: session.access_token,
          });
        } else {
          console.log('[EmailAuthCallback] → PATH: user-info/confirmation (access code flow or no org slug)');
          history.replace(`/activate/${product}/user-info/confirmation`, {
            patientUid,
            flow: 'email',
            accessCode,
          });
        }
      } catch (err) {
        logError('[EmailAuthCallback]', 'Unexpected error', err);
        history.replace(`/activate/${product}/access-code`, {
          error: 'email_verification_failed',
        });
      }
    };

    handleCallback();
  }, [history, product, search, fetchMe]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default EmailAuthCallback;
